
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { CreditCard } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from "@/integrations/supabase/client";

interface PaymentMethodsProps {
  amount: number;
  onSuccess?: () => void;
  onCancel?: () => void;
  isHomeCharger?: boolean;
  hostRevenue?: number;
}

export const PaymentMethods = ({ 
  amount, 
  onSuccess, 
  onCancel, 
  isHomeCharger = false,
  hostRevenue = 0.75
}: PaymentMethodsProps) => {
  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardName] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [csrfToken] = useState(() => Math.random().toString(36).substring(2, 15));
  const { toast } = useToast();
  const navigate = useNavigate();

  const { user } = useAuth();
  const isAuthenticated = !!user;

  // Helper function for calculating fees (placeholder for future use)
  /*
  const calculateFees = (totalAmount: number) => {
    if (isHomeCharger) {
      const hostAmount = totalAmount * hostRevenue;
      const platformFee = totalAmount * (1 - hostRevenue);
      return {
        hostAmount: Number(hostAmount.toFixed(2)),
        platformFee: Number(platformFee.toFixed(2))
      };
    }
    return {
      hostAmount: 0,
      platformFee: totalAmount
    };
  };
  */

  // Enhanced input validation functions
  const validateCardNumber = (number: string): boolean => {
    const cleanNumber = number.replace(/\s/g, '');
    return /^\d{13,19}$/.test(cleanNumber);
  };

  const validateExpiryDate = (expiry: string): boolean => {
    const match = expiry.match(/^(0[1-9]|1[0-2])\/(\d{2})$/);
    if (!match) return false;
    
    const month = parseInt(match[1]);
    const year = parseInt('20' + match[2]);
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    
    return year > currentYear || (year === currentYear && month >= currentMonth);
  };

  const validateCVV = (cvv: string): boolean => {
    return /^\d{3,4}$/.test(cvv);
  };

  const sanitizeInput = (input: string): string => {
    return input.replace(/[<>]/g, '').trim().slice(0, 100);
  };

  const handleStandardPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Enhanced validation
    if (!cardNumber || !cardName || !expiryDate || !cvv) {
      toast({
        title: "Error",
        description: "Please fill in all card details",
        variant: "destructive"
      });
      return;
    }

    if (!validateCardNumber(cardNumber)) {
      toast({
        title: "Invalid Card Number",
        description: "Please enter a valid card number (13-19 digits)",
        variant: "destructive"
      });
      return;
    }

    if (!validateExpiryDate(expiryDate)) {
      toast({
        title: "Invalid Expiry Date",
        description: "Please enter a valid expiry date (MM/YY) that hasn't passed",
        variant: "destructive"
      });
      return;
    }

    if (!validateCVV(cvv)) {
      toast({
        title: "Invalid CVV",
        description: "Please enter a valid CVV (3-4 digits)",
        variant: "destructive"
      });
      return;
    }
    
    try {
      const { data, error } = await supabase.functions.invoke('create-payment', {
        headers: {
          'x-csrf-token': csrfToken
        },
        body: {
          amount: Math.round(amount * 100), // Convert to cents
          stationId: sanitizeInput('default'),
          isHomeCharger,
          hostRevenue
        }
      });

      if (error) throw error;

      // Open Stripe checkout in a new tab
      window.open(data.url, '_blank');
      
      toast({
        title: "Redirecting to Payment",
        description: "Opening Stripe checkout in a new tab...",
      });
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      toast({
        title: "Payment Failed",
        description: "Unable to process payment. Please try again.",
        variant: "destructive"
      });
      console.error('Payment error:', error);
    }
  };

  const handleApplePay = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('create-payment', {
        headers: {
          'x-csrf-token': csrfToken
        },
        body: {
          amount: Math.round(amount * 100),
          stationId: sanitizeInput('default'),
          isHomeCharger,
          hostRevenue
        }
      });

      if (error) throw error;

      // Open Stripe checkout in a new tab
      window.open(data.url, '_blank');
      
      toast({
        title: "Redirecting to Payment",
        description: "Opening Stripe checkout in a new tab...",
      });
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      toast({
        title: "Payment Failed",
        description: "Unable to process Apple Pay. Please try again.",
        variant: "destructive"
      });
      console.error('Apple Pay error:', error);
    }
  };

  const handleGooglePay = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('create-payment', {
        headers: {
          'x-csrf-token': csrfToken
        },
        body: {
          amount: Math.round(amount * 100),
          stationId: sanitizeInput('default'),
          isHomeCharger,
          hostRevenue
        }
      });

      if (error) throw error;

      // Open Stripe checkout in a new tab
      window.open(data.url, '_blank');
      
      toast({
        title: "Redirecting to Payment",
        description: "Opening Stripe checkout in a new tab...",
      });
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      toast({
        title: "Payment Failed",
        description: "Unable to process Google Pay. Please try again.",
        variant: "destructive"
      });
      console.error('Google Pay error:', error);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      toast({
        title: "Authentication Required",
        description: "Please log in to make a payment.",
        variant: "destructive"
      });
      navigate('/login');
      return;
    }
  }, [isAuthenticated, navigate, toast]);

  return (
    <Card className="w-full max-w-md mx-auto animate-fade-in">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold text-taskify-purple">Payment Methods</CardTitle>
        <CardDescription className="text-muted-foreground">
          Choose your preferred payment method
          {isHomeCharger && (
            <div className="mt-2 text-sm font-medium text-taskify-dark-purple">
              {(hostRevenue * 100).toFixed(0)}% of the payment goes to the host
            </div>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div className="flex justify-between items-center p-4 bg-muted rounded-lg">
            <span className="text-lg font-medium">Total Amount:</span>
            <span className="text-lg font-bold text-taskify-purple">${amount.toFixed(2)}</span>
          </div>
          
          <div className="flex flex-col space-y-2">
            <Button 
              variant="outline" 
              className="flex justify-center items-center gap-2 h-12 hover:bg-taskify-light-purple hover:text-taskify-dark-purple transition-colors"
              onClick={handleApplePay}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-apple"><path d="M12 20.94c1.5 0 2.75 1.06 4 1.06 3 0 6-8 6-12.22A4.91 4.91 0 0 0 17 5c-2.22 0-4 1.44-5 2-1-.56-2.78-2-5-2a4.9 4.9 0 0 0-5 4.78C2 14 5 22 8 22c1.25 0 2.5-1.06 4-1.06Z"/><path d="M10 2c1 .5 2 2 2 5"/></svg>
              Pay with Apple Pay
            </Button>
            
            <Button 
              variant="outline" 
              className="flex justify-center items-center gap-2 h-12 hover:bg-taskify-light-purple hover:text-taskify-dark-purple transition-colors"
              onClick={handleGooglePay}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-smartphone"><rect width="14" height="20" x="5" y="2" rx="2" ry="2"/><path d="M12 18h.01"/></svg>
              Pay with Google Pay
            </Button>
          </div>
          
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-muted" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                Or pay with card
              </span>
            </div>
          </div>
          
          <form onSubmit={handleStandardPayment} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cardName">Name on Card</Label>
              <Input 
                id="cardName" 
                placeholder="John Doe" 
                value={cardName}
                onChange={(e) => setCardName(e.target.value)}
                className="h-12"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="cardNumber">Card Number</Label>
              <Input 
                id="cardNumber" 
                placeholder="0000 0000 0000 0000" 
                value={cardNumber}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 19);
                  const formatted = value.replace(/(\d{4})(?=\d)/g, '$1 ');
                  setCardNumber(formatted);
                }}
                className="h-12"
                maxLength={23} // 19 digits + 4 spaces
                pattern="[0-9\s]*"
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="expiryDate">Expiry Date</Label>
                <Input 
                  id="expiryDate" 
                  placeholder="MM/YY" 
                  value={expiryDate}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    const formatted = value.length >= 2 ? `${value.slice(0, 2)}/${value.slice(2, 4)}` : value;
                    setExpiryDate(formatted);
                  }}
                  className="h-12"
                  maxLength={5}
                  pattern="[0-9/]*"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cvv">CVV</Label>
                <Input 
                  id="cvv" 
                  placeholder="123" 
                  value={cvv}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                    setCvv(value);
                  }}
                  className="h-12"
                  maxLength={4}
                  pattern="[0-9]*"
                  required
                />
              </div>
            </div>
            
            <Button type="submit" className="w-full h-12 bg-taskify-purple hover:bg-taskify-dark-purple transition-colors">
              <CreditCard className="mr-2 h-4 w-4" />
              Pay ${amount.toFixed(2)}
            </Button>
          </form>
        </div>
      </CardContent>
      <CardFooter className="flex justify-center">
        <Button 
          variant="ghost" 
          onClick={onCancel}
          className="text-taskify-dark-purple hover:text-taskify-purple hover:bg-taskify-light-purple/50"
        >
          Cancel
        </Button>
      </CardFooter>
    </Card>
  );
};
